import { useContext, useMemo } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { t } from 'i18next'

import { FeedbackContext } from '~/components/feedback'
import Loading from '~/components/Loading'
import Panel from '~/components/Panel'
import { getAccountId } from '~/helpers'
import { useAlert } from '~/hooks/useAlert'
import { adTypesOptions } from '~/modules/retailMedia/constants/adTypesOptions'
import { useAdTypes } from '~/modules/retailMedia/hooks/useAdTypes'
import { postAdTypesService } from '~/modules/retailMedia/services/configuration/ad-types'
import {
  getCampaignPermissionsService,
  getCampaignPermissionsServiceKey
} from '~/modules/retailMedia/services/configuration/campaign-permissions'

import FormEditLink from '../components/FormEditLink'

// import { Container } from './styles';

const PanelPublisherAdTypes: React.FC = () => {
  const { toast } = useContext(FeedbackContext)

  const { data: adTypes = [], error, isPending, queryKey } = useAdTypes()
  const [AlertError] = useAlert(error)

  // Story is offered only to publishers the internal team enabled. Without the
  // flag the checkbox is hidden and a stored `story` is kept on save. A conta entra
  // na key porque o QueryClient sobrevive à troca de conta: sem ela, o checkbox leria
  // a flag da conta anterior.
  const { data: campaignPermissions, error: permissionsError } = useQuery({
    queryKey: [getCampaignPermissionsServiceKey, getAccountId()],
    queryFn: async () => {
      const response = await getCampaignPermissionsService()

      return response?.data
    }
  })
  // A failed fetch hides the checkbox like a publisher without the flag, so it is
  // shown to the admin instead of passing for "Story is not enabled". A failed
  // refetch keeps the last data, whose flag must not keep Story on.
  const [AlertPermissionsError] = useAlert(permissionsError)
  const allowStoryAds =
    !permissionsError && campaignPermissions?.allow_story_ads === true

  const options = useMemo(
    () =>
      adTypesOptions.filter(item => item.value !== 'story' || allowStoryAds),
    [allowStoryAds]
  )

  const initialData = useMemo(
    () => ({
      type: adTypes?.reduce(
        (acc, item) => ({ ...acc, [`type_${item}`]: true }),
        {}
      )
    }),
    [adTypes]
  )

  /**
   * Handle update
   */

  const queryClient = useQueryClient()

  const patchPublisherAdTypesMutation = useMutation({
    mutationFn: postAdTypesService,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey
      })

      toast.success(t('rm:adtype.create.messages.success.message'))
    },
    onError: () => {
      toast.critical(t('common:messages.errorOccurred.message'))
    }
  })

  const handleUpdateSubmit = async (data: FormData) => {
    const checkedAdTypes = data.type.all
      .filter(item => !!item.checked)
      .map(item => item.value)

    const body: AdvertiserPublisherLinkPatchRequestBody = {
      ad_types:
        !allowStoryAds && adTypes.includes('story')
          ? [...checkedAdTypes, 'story']
          : checkedAdTypes
    }

    await patchPublisherAdTypesMutation.mutateAsync(body)
  }

  return (
    <Panel
      title={t('rm:adtype.panel.title')}
      description={t('rm:adtype.panel.description')}
    >
      <Loading status={isPending}>{t('common:actions.loadingData')}</Loading>

      <AlertError />
      <AlertPermissionsError />

      <FormEditLink
        options={options}
        initialData={initialData}
        onSubmit={handleUpdateSubmit}
        fields={['adTypes']}
        isLoading={patchPublisherAdTypesMutation.isPending}
      />
    </Panel>
  )
}

export default PanelPublisherAdTypes
